/* iter87.199.96 — Billing success landing.

Paddle's client-side checkout overlay lands here after a successful
subscription. The webhook grants Pro+ asynchronously (usually
<5 seconds), so we poll `/api/rivalry-pro/me` a few times before
showing the "you're all set" state.

iter87.203 — Was a hard-stop after 10 attempts (~15s): once hit, the
page gave up polling entirely and required a manual click, so a
webhook that succeeded even a few seconds late looked like a
permanent hang to the customer (real-world report: charged, stuck on
"Provisioning", had to be manually rescued). Now split into three
phases by elapsed time — fast poll, then a slower background poll
that keeps going (with a reassuring message + a manual fallback for
anyone who wants to force it), and only a final real dead-end after
3 minutes. Success auto-transitions to "live" at any point, no
manual step required. */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ClerkProvider, useAuth } from "@clerk/react";
import { Loader2, Trophy, RotateCw } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const CLERK_PK = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;

const FAST_WINDOW_MS = 15_000;   // provisioning copy, poll every 1.5s
const GIVE_UP_MS = 180_000;      // 3 min total, poll every 5s after the fast window

function BillingSuccessInner() {
    const { getToken, isSignedIn, isLoaded } = useAuth();
    const [state, setState] = useState("provisioning"); // provisioning | slow | live | timeout
    useEffect(() => {
        // iter87.199.138 — Don't start polling until Clerk finishes
        // loading. This page mounts its OWN ClerkProvider (see bottom
        // of file), which needs a beat to rehydrate the session after
        // the hard `window.location.assign` navigation from the Paddle
        // overlay. The old effect captured `isSignedIn` (false/undefined
        // pre-load) in a `[]`-deps closure that never re-ran, so every
        // poll silently dropped the Bearer token and 401'd for all 10
        // attempts even once Clerk WAS loaded and the tier HAD flipped.
        if (!isLoaded) return;
        let cancelled = false;
        let timer = null;
        const startedAt = Date.now();
        const tick = async () => {
            if (cancelled) return;
            try {
                // iter87.199.99c — Must attach Clerk Bearer token; cookies
                // alone aren't accepted by `/api/rivalry-pro/me`. Without
                // this the poll silently 401s and the page cycles between
                // provisioning and timeout even though the tier IS flipped.
                const token = isSignedIn ? await getToken() : null;
                const r = await fetch(`${API}/api/rivalry-pro/me`, {
                    credentials: "include",
                    headers: token ? { Authorization: `Bearer ${token}` } : {},
                });
                if (r.ok) {
                    const me = await r.json();
                    if (me?.tier === "pro_plus" || me?.effective_tier === "paid") {
                        setState("live");
                        toast.success("Pro+ active — welcome to the crew");
                        return;
                    }
                }
            } catch { /* ignore, keep polling */ }
            const elapsed = Date.now() - startedAt;
            if (elapsed >= GIVE_UP_MS) {
                setState("timeout");
                return;
            }
            setState(elapsed >= FAST_WINDOW_MS ? "slow" : "provisioning");
            timer = setTimeout(tick, elapsed >= FAST_WINDOW_MS ? 5000 : 1500);
        };
        tick();
        return () => { cancelled = true; if (timer) clearTimeout(timer); };
    }, [isLoaded, isSignedIn, getToken]);


    return (
        <div className="min-h-screen bg-black text-cyber flex items-center justify-center p-6"
             data-testid="billing-success-page">
            <div className="max-w-lg w-full brutal-card border-cyber/40 p-8 space-y-6 text-center">
                {state === "provisioning" && (
                    <>
                        <Loader2 className="w-6 h-6 mx-auto text-cyber animate-spin" strokeWidth={1.5} />
                        <h1 className="text-4xl font-black tracking-tight" data-testid="billing-success-provisioning">
                            Provisioning Pro+
                        </h1>
                        <p className="text-sm text-white/70">
                            Payment received. We're activating your Pro+ perks — usually under 5 seconds.
                        </p>
                        <div className="text-xs text-white/40">Do not close this page.</div>
                    </>
                )}
                {state === "live" && (
                    <>
                        <Trophy className="w-6 h-6 mx-auto text-neon" strokeWidth={1.5} />
                        <h1 className="text-4xl font-black tracking-tight text-neon" data-testid="billing-success-live">
                            You're Pro+
                        </h1>
                        <p className="text-sm text-white/70">
                            Ride Stories, Season Recaps, Living Rank Charts — all unlocked across every rivalry you run.
                        </p>
                        <Link to="/rivalry-pro"
                              className="inline-block px-6 py-3 border-2 border-neon text-neon font-black hover:bg-neon hover:text-black transition-colors"
                              data-testid="billing-success-continue">
                            Continue to your rivalries →
                        </Link>
                    </>
                )}
                {state === "slow" && (
                    <>
                        <Loader2 className="w-6 h-6 mx-auto text-cyber animate-spin" strokeWidth={1.5} />
                        <h1 className="text-4xl font-black tracking-tight" data-testid="billing-success-slow">
                            Still working on it
                        </h1>
                        <p className="text-sm text-white/70">
                            Payment received — provisioning is taking a little longer than usual. Keep this
                            page open, we'll jump you in the moment it's ready.
                        </p>
                        <button onClick={() => window.location.reload()}
                                className="px-6 py-3 border-2 border-cyber text-cyber font-black hover:bg-cyber hover:text-black transition-colors"
                                data-testid="billing-success-slow-refresh">
                            Refresh now
                        </button>
                    </>
                )}
                {state === "timeout" && (
                    <>
                        <RotateCw className="w-6 h-6 mx-auto text-white/60" strokeWidth={1.5} />
                        <h1 className="text-4xl font-black tracking-tight">
                            Payment received — one moment
                        </h1>
                        <p className="text-sm text-white/70">
                            Our provisioning webhook is a little slow today. Refresh in a few seconds, or reach out to support if this persists.
                        </p>
                        <button onClick={() => window.location.reload()}
                                className="px-6 py-3 border-2 border-cyber text-cyber font-black hover:bg-cyber hover:text-black transition-colors"
                                data-testid="billing-success-refresh">
                            Refresh
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}

/* iter87.199.99c — Top-level export wraps the inner component in its
   own ClerkProvider. `/billing/success` is a top-level route (not
   nested under ProShell) so that LS's redirect URL doesn't need a
   `/rivalry-pro` prefix — but `useAuth()` still needs a provider
   ancestor to attach the Bearer token to the tier poll. */
export default function BillingSuccess() {
    if (!CLERK_PK) {
        // No Clerk key configured — still render the page, poll will
        // fail auth silently and the timeout branch is shown.
        return <BillingSuccessInner />;
    }
    return (
        <ClerkProvider publishableKey={CLERK_PK}>
            <BillingSuccessInner />
        </ClerkProvider>
    );
}
